import type { Dict } from '../../i18n';

const dict: Dict = {
  en: {
    // Page headings
    createTitle: 'New client',
    editTitle: 'Edit client',
    createSubtitle: 'Add a new clinic to your address book',
    editSubtitle: 'Update this clinic’s details',

    // Loading / error
    loading: 'Loading client…',
    loadError: 'Could not load the client.',

    // Core fields
    fieldClientType: 'Client type',
    typeCompany: 'Company',
    typePerson: 'Person',
    fieldCompany: 'Company name',
    fieldCompanyPlaceholder: 'Clínica Sole',
    fieldPersonName: 'Person name',
    fieldPersonNamePlaceholder: 'María García',
    fieldTradeName: 'Trade name',
    fieldTradeNamePlaceholder: 'Clínica Sole',
    fieldCifVatEs: 'NIF/CIF',
    fieldCifVatIntl: 'National number (not VAT)',
    fieldVatNumber: 'VAT number',
    vatValidateBtn: 'Validate',
    vatChecking: 'Checking…',
    vatValid: 'VAT valid',
    vatInvalid: 'VAT invalid',
    vatUnavailable: 'VIES unavailable — try later',
    vatNotApplicable: 'Non-EU — not checked',
    vatUnverified: 'Not validated yet',
    vatBlockedHint: 'Fix or remove the VAT number to save.',
    vatNoVatHint: 'Without a VAT number, products are sold with VAT (21%).',
    fieldEmail: 'Email',
    fieldEmailPlaceholder: 'info@clinicasole.es',
    fieldPhone: 'Phone number',
    fieldPhoneCountry: 'Country code',
    fieldPhonePlaceholder: '600 000 000',

    // Contact person
    contactPersonHeading: 'Contact person',
    fieldFirstName: 'First name',
    fieldLastName: 'Last name',
    fieldClinicDoctorName: 'Clinic / doctor name',
    fieldCollegiateNumber: 'Collegiate number',

    // Owner agent (admin only)
    ownerAgentHeading: 'Owner agent',
    ownerAgentLabel: 'Assigned agent',
    ownerAgentUnassigned: 'Unassigned',
    ownerAgentSelect: 'Select an agent…',
    confirmSave: 'Save the changes to this client?',
    confirmReassign: 'Reassign this client to {{agent}} and save the changes?',
    confirmRemoveDelivery: 'Remove this delivery address?',

    // Fiscal details
    fiscalHeading: 'Fiscal details',

    // Addresses
    billingAddressHeading: 'Billing address',
    fieldAddressSearch: 'Search address',
    fieldAddressSearchPlaceholder: 'Start typing an address…',
    fieldAddress: 'Address',
    fieldAddress2: 'Address 2',
    fieldCity: 'City',
    fieldProvince: 'State / Province / Region',
    fieldPostalCode: 'ZIP / Postal code',
    fieldCountry: 'Country',

    // Delivery
    deliveryAddressesHeading: 'Delivery addresses',
    deliverySameAsBilling: 'Delivery address is the same as billing',
    fieldLabel: 'Label (optional)',
    fieldLabelPlaceholder: 'Main clinic, Warehouse…',
    deliveryAddressN: 'Delivery address {{n}}',
    addDeliveryAddress: '+ Add delivery address',
    removeDeliveryAddress: 'Remove',
    deliveryHint:
      'Add one or more addresses where orders for this client can be shipped.',

    // Validation
    errRequired: 'This field is required.',
    errEmail: 'Enter a valid email address.',
    errPhone: 'Enter a valid phone number.',
    errCountry: 'Select a country.',
    errAtLeastOneDelivery: 'Add at least one delivery address.',
    // Profile (source, commercial condition, language, SEPA)
    profileHeading: 'Profile',
    fieldSource: 'Source / channel of acquisition',
    sourceNone: '— not set —',
    source_CONGRESS: 'Congress',
    source_WORKSHOP: 'Workshop',
    source_REFERRAL: 'Referral',
    source_WEB: 'Web',
    source_AGENT_VISIT: 'Agent visit',
    source_CAMPAIGN: 'Campaign',
    source_OTHER: 'Other',
    fieldCommercialCondition: 'Commercial condition',
    condition_ESTANDAR: 'Standard',
    condition_LARGO_PLAZO: 'Long term',
    condition_TRAINER: 'Trainer',
    fieldLanguage: 'Language of client messages',
    languageNotSet: '— not set —',
    fieldSepaEligible: 'SEPA eligible',
    sepaEligibleHint: 'Set by the Super Admin only.',
    // Tax number status
    taxValid: 'Tax number valid',
    taxInvalid: 'Tax number invalid — the client is blocked for orders until it is corrected',
    taxMissing: 'Tax number missing — the client is blocked for orders until it is filled in',
    taxUnverified: 'Tax number not confirmed by VIES yet — the client is blocked for orders',
    errTaxType: 'A CIF belongs to a company, a NIF / NIE to a person — change the client type.',
    // Billing entities
    billingEntitiesHeading: 'Other billing entities',
    billingEntitiesHint:
      'Legal entities, besides the one above, the proforma and the invoice can be issued to (each with its own tax number).',
    billingEntityN: 'Billing entity {{n}}',
    addBillingEntity: '+ Add billing entity',
    removeBillingEntity: 'Remove',
    fieldLegalName: 'Legal name',
    // Delivery contact
    fieldDeliveryContactName: 'Contact name at this address',
    fieldDeliveryContactPhone: 'Contact phone (for the carrier)',
    // Duplicate warning
    duplicateWarning:
      'Another client already uses this {{field}} ({{count}} match). Save anyway?',
    duplicateFieldEmail: 'e-mail',
    duplicateFieldPhone: 'phone',

    // Buttons / status
    cancelBtn: 'Cancel',
    savingBtn: 'Saving…',
    createClientBtn: 'Create client',
    saveChangesBtn: 'Save changes',
  },
  es: {
    // Page headings
    createTitle: 'Nuevo cliente',
    editTitle: 'Editar cliente',
    createSubtitle: 'Añade una nueva clínica a tu agenda',
    editSubtitle: 'Actualiza los datos de esta clínica',

    // Loading / error
    loading: 'Cargando cliente…',
    loadError: 'No se pudo cargar el cliente.',

    // Core fields
    fieldClientType: 'Tipo de cliente',
    typeCompany: 'Empresa',
    typePerson: 'Persona',
    fieldCompany: 'Nombre de la empresa',
    fieldCompanyPlaceholder: 'Clínica Sole',
    fieldPersonName: 'Nombre de la persona',
    fieldPersonNamePlaceholder: 'María García',
    fieldTradeName: 'Nombre comercial',
    fieldTradeNamePlaceholder: 'Clínica Sole',
    fieldCifVatEs: 'NIF/CIF',
    fieldCifVatIntl: 'Número nacional (no IVA)',
    fieldVatNumber: 'Número de IVA',
    vatValidateBtn: 'Validar',
    vatChecking: 'Comprobando…',
    vatValid: 'IVA válido',
    vatInvalid: 'IVA no válido',
    vatUnavailable: 'VIES no disponible — reintenta',
    vatNotApplicable: 'No UE — sin comprobar',
    vatUnverified: 'Aún sin validar',
    vatBlockedHint: 'Corrige o elimina el número de IVA para guardar.',
    vatNoVatHint: 'Sin número de IVA, los productos se venden con IVA (21%).',
    fieldEmail: 'Correo electrónico',
    fieldEmailPlaceholder: 'info@clinicasole.es',
    fieldPhone: 'Número de teléfono',
    fieldPhoneCountry: 'Código de país',
    fieldPhonePlaceholder: '600 000 000',

    // Contact person
    contactPersonHeading: 'Persona de contacto',
    fieldFirstName: 'Nombre',
    fieldLastName: 'Apellido',
    fieldClinicDoctorName: 'Nombre de la clínica / doctor',
    fieldCollegiateNumber: 'Número de colegiado',

    // Agente propietario (solo admin)
    ownerAgentHeading: 'Agente propietario',
    ownerAgentLabel: 'Agente asignado',
    ownerAgentUnassigned: 'Sin asignar',
    ownerAgentSelect: 'Selecciona un agente…',
    confirmSave: '¿Guardar los cambios de este cliente?',
    confirmReassign: '¿Reasignar este cliente a {{agent}} y guardar los cambios?',
    confirmRemoveDelivery: '¿Eliminar esta dirección de entrega?',

    // Datos fiscales
    fiscalHeading: 'Datos fiscales',

    // Addresses
    billingAddressHeading: 'Dirección de facturación',
    fieldAddressSearch: 'Buscar dirección',
    fieldAddressSearchPlaceholder: 'Empieza a escribir una dirección…',
    fieldAddress: 'Dirección',
    fieldAddress2: 'Dirección 2',
    fieldCity: 'Ciudad',
    fieldProvince: 'Estado / Provincia / Región',
    fieldPostalCode: 'ZIP / Código Postal',
    fieldCountry: 'País',

    // Delivery
    deliveryAddressesHeading: 'Direcciones de entrega',
    deliverySameAsBilling: 'La dirección de entrega es la misma que la de facturación',
    fieldLabel: 'Etiqueta (opcional)',
    fieldLabelPlaceholder: 'Clínica principal, Almacén…',
    deliveryAddressN: 'Dirección de entrega {{n}}',
    addDeliveryAddress: '+ Añadir dirección de entrega',
    removeDeliveryAddress: 'Eliminar',
    deliveryHint:
      'Añade una o más direcciones donde se puedan enviar los pedidos de este cliente.',

    // Validation
    errRequired: 'Este campo es obligatorio.',
    errEmail: 'Introduce un correo electrónico válido.',
    errPhone: 'Introduce un número de teléfono válido.',
    errCountry: 'Selecciona un país.',
    errAtLeastOneDelivery: 'Añade al menos una dirección de entrega.',
    // Perfil
    profileHeading: 'Perfil',
    fieldSource: 'Origen / canal de captación',
    sourceNone: '— sin definir —',
    source_CONGRESS: 'Congreso',
    source_WORKSHOP: 'Workshop',
    source_REFERRAL: 'Recomendación',
    source_WEB: 'Web',
    source_AGENT_VISIT: 'Visita del agente',
    source_CAMPAIGN: 'Campaña',
    source_OTHER: 'Otro',
    fieldCommercialCondition: 'Condición comercial',
    condition_ESTANDAR: 'Estándar',
    condition_LARGO_PLAZO: 'Largo plazo',
    condition_TRAINER: 'Trainer',
    fieldLanguage: 'Idioma de los mensajes al cliente',
    languageNotSet: '— sin definir —',
    fieldSepaEligible: 'Apto para SEPA',
    sepaEligibleHint: 'Solo lo establece el Super Admin.',
    taxValid: 'Número fiscal válido',
    taxInvalid: 'Número fiscal no válido — el cliente queda bloqueado para pedidos hasta corregirlo',
    taxMissing: 'Falta el número fiscal — el cliente queda bloqueado para pedidos hasta rellenarlo',
    taxUnverified: 'Número fiscal aún no confirmado por VIES — el cliente queda bloqueado para pedidos',
    errTaxType: 'Un CIF corresponde a una empresa y un NIF / NIE a una persona — cambia el tipo de cliente.',
    billingEntitiesHeading: 'Otras entidades de facturación',
    billingEntitiesHint:
      'Entidades jurídicas, además de la anterior, a las que se puede emitir la proforma y la factura (cada una con su número fiscal).',
    billingEntityN: 'Entidad de facturación {{n}}',
    addBillingEntity: '+ Añadir entidad de facturación',
    removeBillingEntity: 'Quitar',
    fieldLegalName: 'Razón social',
    fieldDeliveryContactName: 'Persona de contacto en esta dirección',
    fieldDeliveryContactPhone: 'Teléfono de contacto (para el transportista)',
    duplicateWarning:
      'Otro cliente ya usa este {{field}} ({{count}} coincidencia). ¿Guardar de todos modos?',
    duplicateFieldEmail: 'correo',
    duplicateFieldPhone: 'teléfono',

    // Buttons / status
    cancelBtn: 'Cancelar',
    savingBtn: 'Guardando…',
    createClientBtn: 'Crear cliente',
    saveChangesBtn: 'Guardar cambios',
  },
  de: {
    // Page headings
    createTitle: 'Neuer Kunde',
    editTitle: 'Kunde bearbeiten',
    createSubtitle: 'Fügen Sie Ihrem Adressbuch eine neue Klinik hinzu',
    editSubtitle: 'Aktualisieren Sie die Daten dieser Klinik',

    // Loading / error
    loading: 'Kunde wird geladen…',
    loadError: 'Der Kunde konnte nicht geladen werden.',

    // Core fields
    fieldClientType: 'Kundentyp',
    typeCompany: 'Unternehmen',
    typePerson: 'Person',
    fieldCompany: 'Firmenname',
    fieldCompanyPlaceholder: 'Clínica Sole',
    fieldPersonName: 'Name der Person',
    fieldPersonNamePlaceholder: 'María García',
    fieldTradeName: 'Handelsname',
    fieldTradeNamePlaceholder: 'Clínica Sole',
    fieldCifVatEs: 'NIF/CIF',
    fieldCifVatIntl: 'Nationale Nummer (keine USt-IdNr.)',
    fieldVatNumber: 'USt-IdNr.',
    vatValidateBtn: 'Prüfen',
    vatChecking: 'Wird geprüft…',
    vatValid: 'USt-IdNr. gültig',
    vatInvalid: 'USt-IdNr. ungültig',
    vatUnavailable: 'VIES nicht verfügbar — später erneut versuchen',
    vatNotApplicable: 'Nicht-EU — nicht geprüft',
    vatUnverified: 'Noch nicht geprüft',
    vatBlockedHint: 'Korrigieren oder entfernen Sie die USt-IdNr., um zu speichern.',
    vatNoVatHint: 'Ohne USt-IdNr. werden Produkte mit MwSt. (21 %) verkauft.',
    fieldEmail: 'E-Mail',
    fieldEmailPlaceholder: 'info@clinicasole.es',
    fieldPhone: 'Telefonnummer',
    fieldPhoneCountry: 'Ländervorwahl',
    fieldPhonePlaceholder: '600 000 000',

    // Contact person
    contactPersonHeading: 'Ansprechpartner',
    fieldFirstName: 'Vorname',
    fieldLastName: 'Nachname',
    fieldClinicDoctorName: 'Klinik-/Arztname',
    fieldCollegiateNumber: 'Kammernummer',

    // Owner agent (admin only)
    ownerAgentHeading: 'Zuständiger Agent',
    ownerAgentLabel: 'Zugewiesener Agent',
    ownerAgentUnassigned: 'Nicht zugewiesen',
    ownerAgentSelect: 'Wählen Sie einen Agenten aus…',
    confirmSave: 'Die Änderungen an diesem Kunden speichern?',
    confirmReassign: 'Diesen Kunden {{agent}} neu zuweisen und die Änderungen speichern?',
    confirmRemoveDelivery: 'Diese Lieferadresse entfernen?',

    // Fiscal details
    fiscalHeading: 'Steuerdaten',

    // Addresses
    billingAddressHeading: 'Rechnungsadresse',
    fieldAddressSearch: 'Adresse suchen',
    fieldAddressSearchPlaceholder: 'Adresse eingeben…',
    fieldAddress: 'Adresse',
    fieldAddress2: 'Adresse 2',
    fieldCity: 'Stadt',
    fieldProvince: 'Bundesland/Provinz/Region',
    fieldPostalCode: 'PLZ / Postleitzahl',
    fieldCountry: 'Land',

    // Delivery
    deliveryAddressesHeading: 'Lieferadressen',
    deliverySameAsBilling: 'Die Lieferadresse entspricht der Rechnungsadresse',
    fieldLabel: 'Bezeichnung (optional)',
    fieldLabelPlaceholder: 'Hauptklinik, Lager…',
    deliveryAddressN: 'Lieferadresse {{n}}',
    addDeliveryAddress: '+ Lieferadresse hinzufügen',
    removeDeliveryAddress: 'Entfernen',
    deliveryHint:
      'Fügen Sie eine oder mehrere Adressen hinzu, an die Bestellungen für diesen Kunden geliefert werden können.',

    // Validation
    errRequired: 'Dieses Feld ist erforderlich.',
    errEmail: 'Geben Sie eine gültige E-Mail-Adresse ein.',
    errPhone: 'Geben Sie eine gültige Telefonnummer ein.',
    errCountry: 'Wählen Sie ein Land aus.',
    errAtLeastOneDelivery: 'Fügen Sie mindestens eine Lieferadresse hinzu.',
    // Profil
    profileHeading: 'Profil',
    fieldSource: 'Quelle / Akquisekanal',
    sourceNone: '— nicht gesetzt —',
    source_CONGRESS: 'Kongress',
    source_WORKSHOP: 'Workshop',
    source_REFERRAL: 'Empfehlung',
    source_WEB: 'Web',
    source_AGENT_VISIT: 'Agentenbesuch',
    source_CAMPAIGN: 'Kampagne',
    source_OTHER: 'Sonstiges',
    fieldCommercialCondition: 'Kommerzielle Kondition',
    condition_ESTANDAR: 'Standard',
    condition_LARGO_PLAZO: 'Langfristig',
    condition_TRAINER: 'Trainer',
    fieldLanguage: 'Sprache der Kundennachrichten',
    languageNotSet: '— nicht gesetzt —',
    fieldSepaEligible: 'SEPA-berechtigt',
    sepaEligibleHint: 'Wird nur vom Super Admin gesetzt.',
    taxValid: 'Steuernummer gültig',
    taxInvalid: 'Steuernummer ungültig — der Kunde ist bis zur Korrektur für Bestellungen gesperrt',
    taxMissing: 'Steuernummer fehlt — der Kunde ist bis zur Eingabe für Bestellungen gesperrt',
    taxUnverified: 'Steuernummer noch nicht per VIES bestätigt — der Kunde ist für Bestellungen gesperrt',
    errTaxType: 'Eine CIF gehört zu einem Unternehmen, eine NIF / NIE zu einer Person — ändern Sie den Kundentyp.',
    billingEntitiesHeading: 'Weitere Rechnungsempfänger',
    billingEntitiesHint:
      'Juristische Personen neben der obigen, an die Proforma und Rechnung gestellt werden können (jede mit eigener Steuernummer).',
    billingEntityN: 'Rechnungsempfänger {{n}}',
    addBillingEntity: '+ Rechnungsempfänger hinzufügen',
    removeBillingEntity: 'Entfernen',
    fieldLegalName: 'Firmierung',
    fieldDeliveryContactName: 'Ansprechpartner an dieser Adresse',
    fieldDeliveryContactPhone: 'Kontakttelefon (für den Spediteur)',
    duplicateWarning:
      'Ein anderer Kunde verwendet bereits diese {{field}} ({{count}} Treffer). Trotzdem speichern?',
    duplicateFieldEmail: 'E-Mail',
    duplicateFieldPhone: 'Telefonnummer',

    // Buttons / status
    cancelBtn: 'Abbrechen',
    savingBtn: 'Speichert…',
    createClientBtn: 'Kunde anlegen',
    saveChangesBtn: 'Änderungen speichern',
  },
};

export default dict;
